import { getEnvelopedListPageTotal } from 'features/common/helpers';
import { IEnvelopeList } from 'features/common/types';
import { ApiService } from 'services';
import {
	IPrechargedServiceListResponse,
	ICitiesResponse,
	IPrechargedServiceFilters,
	IPrechargedService,
	IServiceTypesResponse,
} from './types';

export const getPrechargedServices = async (
	page = 0,
	size = 15,
	filters: IPrechargedServiceFilters = {},
): Promise<IPrechargedServiceListResponse> => {
	try {
		// Construir query parameters
		const queryParams = new URLSearchParams({
			page: page.toString(),
			size: size.toString(),
			envelope: 'true',
		});

		// Agregar filtros solo si tienen valor
		if (filters.destinationCodes) queryParams.append('destinationCodes', filters.destinationCodes);
		if (filters.name) queryParams.append('name', filters.name);
		if (filters.serviceTypeName) queryParams.append('serviceTypeName', filters.serviceTypeName);
		if (filters.enable !== undefined) queryParams.append('enable', filters.enable.toString());
		if (filters.observations) queryParams.append('observations', filters.observations);
		if (filters.lastUpdate) queryParams.append('lastUpdate', filters.lastUpdate);
		if (filters.forAllDestinations !== undefined)
			queryParams.append('forAllDestinations', filters.forAllDestinations.toString());
		if (filters.availableInQuotation !== undefined)
			queryParams.append('availableInQuotation', filters.availableInQuotation.toString());

		const response = await ApiService.get(`/v1/mevuelo/backend/packageservices?${queryParams.toString()}`);

		// Crear estructura compatible con IEnvelopeList
		const envelopedList: IEnvelopeList<IPrechargedService> = {
			data: response.data.data || [],
			status: response.status,
			headers: {
				'X-Page': parseInt(response.headers['x-page'] || page, 10),
				'X-Page-From': parseInt(response.headers['x-page-from'] || '0', 10),
				'X-Page-Size': parseInt(response.headers['x-page-size'] || size, 10),
				'X-Page-Total': parseInt(response.headers['x-page-total'] || '0', 10), // Total de elementos
			},
		};

		// Usar la función helper para calcular páginas totales
		const totalPages = getEnvelopedListPageTotal(envelopedList);

		return {
			data: envelopedList.data,
			page: envelopedList.headers['X-Page'],
			pageTotal: totalPages, // Páginas calculadas correctamente
			size: envelopedList.headers['X-Page-Size'],
			total: envelopedList.headers['X-Page-Total'], // Total de elementos
		};
	} catch (error) {
		console.error('Error fetching precharged services:', error);
		throw error;
	}
};

export const getPrechargedServiceById = async (id: number): Promise<IPrechargedService> => {
	try {
		const response = await ApiService.get(`v1/mevuelo/backend/packageservices/${id}`);

		return response.data;
	} catch (error) {
		console.error('Error fetching precharged service:', error);
		throw error;
	}
};

export const getCities = async (): Promise<ICitiesResponse> => {
	try {
		const response = await ApiService.get('v1/mevuelo/backend/places/cities');

		return {
			data: response.data.data || response.data || [],
		};
	} catch (error) {
		console.error('Error fetching cities:', error);
		throw error;
	}
};

export const getServiceTypes = async (): Promise<IServiceTypesResponse> => {
	try {
		const response = await ApiService.get('v1/mevuelo/backend/packageservicetypes');

		return {
			data: response.data || [],
		};
	} catch (error) {
		console.error('Error fetching service types:', error);
		throw error;
	}
};

export const createPrechargedService = async (serviceData: IPrechargedService): Promise<number> => {
	try {
		const response = await ApiService.post('v1/mevuelo/backend/packageservices', serviceData);

		return response.data; // Retorna solo el ID del nuevo servicio
	} catch (error) {
		console.error('Error creating precharged service:', error);
		throw error;
	}
};

export const updatePrechargedService = async (
	id: number,
	serviceData: Partial<IPrechargedService>,
): Promise<IPrechargedService> => {
	try {
		const response = await ApiService.put(`v1/mevuelo/backend/packageservices/${id}`, serviceData);

		return response.data;
	} catch (error) {
		console.error('Error updating precharged service:', error);
		throw error;
	}
};
